/**
 * Single source of truth for the plausible range of each numeric assumption.
 *
 * Consumed by BOTH the assumption editor (inline field error) and `validate()` (the gate every scenario
 * passes before compute() runs, including imports), so a value the UI would flag can never reach the
 * engine silently — validate() was previously looser than the editor (e.g. a −50% discount rate or a
 * 0.01% cap rate passed with zero errors and produced a $291 trillion NPV / $133 billion realisation).
 *
 * `undefined` is deliberately NOT treated as a flagged value here: required inputs are rejected by
 * validate()'s required-field sweep, and the bounded keys that may legitimately be absent
 * (schoolWacc, incentiveAmortisationYears, developerExitYear, cpiAssumption, marketReviewPct,
 * targetEnrolment, enrolmentCaptureRate, approvalCostReddamSharePct) carry documented runtime
 * defaults in types.ts so scenarios saved before those fields existed still load.
 */
import type { Assumptions } from "./types";

export const NOT_A_NUMBER = "Must be a number";
export type AssumptionBound = {
  min?: number;
  max?: number;
  /** min is exclusive (value must be strictly greater) */
  minExclusive?: boolean;
  /** max is exclusive (value must be strictly less) */
  maxExclusive?: boolean;
  integer?: boolean;
  /** Editor message; also used verbatim by validate(). */
  message: string;
};

export const ASSUMPTION_BOUNDS = {
  discountRate: {
    min: 0,
    max: 1,
    minExclusive: true,
    maxExclusive: true,
    message: "Must be between 0% and 100%",
  },
  developerHurdleIRR: {
    min: 0,
    max: 1,
    minExclusive: true,
    maxExclusive: true,
    message: "Must be between 0% and 100%",
  },
  tuitionGrowthPct: {
    min: 0,
    max: 1,
    maxExclusive: true,
    message: "Must be between 0% and 100%",
  },
  rentIndexationPct: {
    min: 0,
    max: 1,
    maxExclusive: true,
    message: "Must be between 0% and 100%",
  },
  cpiAssumption: {
    min: 0,
    max: 0.5,
    maxExclusive: true,
    message: "Must be between 0% and 50%",
  },
  marketReviewPct: {
    min: 0,
    max: 0.5,
    maxExclusive: true,
    message: "Must be between 0% and 50%",
  },
  developerExitYear: {
    min: 1,
    max: 50,
    integer: true,
    message: "Must be 1–50 years",
  },
  startingUtilisation: {
    min: 0,
    max: 1,
    minExclusive: true,
    message: "Must be between 0% and 100%",
  },
  steadyStateUtilisation: {
    min: 0,
    max: 1,
    minExclusive: true,
    message: "Must be between 0% and 100%",
  },
  targetEnrolment: { min: 0, message: "Must be ≥ 0" },
  enrolmentCaptureRate: {
    min: 0,
    max: 1,
    minExclusive: true,
    message: "Must be between 0% and 100%",
  },
  modelYears: { min: 5, max: 50, integer: true, message: "Must be 5–50 years" },
  leaseTermYears: {
    min: 5,
    max: 99,
    integer: true,
    message: "Must be 5–99 years",
  },
  developmentMonths: {
    min: 6,
    max: 60,
    integer: true,
    message: "Must be 6–60 months",
  },
  // A cap rate below 1% is not a plausible valuation input (a 0.01% rate turns a $13M NOI into a $133B
  // realisation); the floor is deliberately generous versus education real estate (~5–7%).
  capRate: {
    min: 0.01,
    max: 0.5,
    maxExclusive: true,
    message: "Must be between 1% and 50%",
  },
  schoolWacc: {
    min: 0,
    max: 1,
    maxExclusive: true,
    message: "Must be ≥ 0% (negative WACC causes division by zero in NPV)",
  },
  incentiveAmortisationYears: {
    min: 1,
    integer: true,
    message: "Must be a whole number ≥ 1 year",
  },
  // loop#011 tier-1 finding 3: these four are silently clamped/defaulted by compute()
  // (or feed live debt sizing) — validate() must flag out-of-range values at the gate.
  approvalCostReddamSharePct: {
    min: 0,
    max: 1,
    message: "Must be between 0% and 100%",
  },
  fitoutAmortisationYears: {
    min: 1,
    integer: true,
    message: "Must be a whole number ≥ 1 year",
  },
  constructionLvrRate: {
    min: 0,
    max: 1,
    message: "Must be between 0% and 100%",
  },
  termLoanLvrRate: {
    min: 0,
    max: 1,
    message: "Must be between 0% and 100%",
  },
  // loop#025: the four stage shares are clamped by normaliseShares (negatives → 0,
  // survivors scaled up) — with compensating shares the mix total can sit at exactly
  // 100% while one stage is silently deleted. Bounded individually, like the other
  // clamped share inputs (approvalCostReddamSharePct).
  earlyLearningShare: {
    min: 0,
    max: 1,
    message: "Must be between 0% and 100%",
  },
  primaryShare: {
    min: 0,
    max: 1,
    message: "Must be between 0% and 100%",
  },
  secondaryShare: {
    min: 0,
    max: 1,
    message: "Must be between 0% and 100%",
  },
  seniorShare: {
    min: 0,
    max: 1,
    message: "Must be between 0% and 100%",
  },
  coveredPlayAreaGroundRent: { min: 0, message: "Must be ≥ 0" },
  coveredPlayAreaConstructionCost: { min: 0, message: "Must be ≥ 0" },
} satisfies Partial<Record<keyof Assumptions, AssumptionBound>>;

/** Returns the bound's message when `value` is outside the plausible range for `key`, else undefined. */
export function assumptionBoundError(
  key: string,
  value: unknown,
): string | undefined {
  const bound = (
    ASSUMPTION_BOUNDS as Record<string, AssumptionBound | undefined>
  )[key];
  if (!bound) return undefined;
  // null / "" / non-numeric strings must not coerce to 0 and slip past a `min: 0` bound;
  // NaN / ±Infinity get a message that says what is wrong instead of quoting the range.
  const v =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;
  if (!Number.isFinite(v)) return NOT_A_NUMBER;
  if (bound.integer && !Number.isInteger(v)) return bound.message;
  if (
    bound.min !== undefined &&
    (bound.minExclusive ? v <= bound.min : v < bound.min)
  )
    return bound.message;
  if (
    bound.max !== undefined &&
    (bound.maxExclusive ? v >= bound.max : v > bound.max)
  )
    return bound.message;
  return undefined;
}
